/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Box,
  alpha,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Button,
  Tooltip,
  IconButton,
  Chip,
  Checkbox,
  ListItemText,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import FormButtonControls from '@/components/forms/formButtonControls/FormButtonControls';
import tenant from '@/services/TenantService';
import ToastMessage from '@/components/toasts/ToastMessage';
import { toast } from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';

const HYPERVISOR_TYPES = {
  VMWARE: 'vmware',
  HYPERV: 'hyper-v',
  // PROXMOX: 'proxmox',
};

const HypervisorInterfacesConfig = ({
  next,
  back,
  isLastStep,
  isFirstStep,
  handleCancel,
  deployment,
}) => {
  const [data, setData] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [selectedHypervisorInterfaces, setSelectedHypervisorInterfaces] =
    useState(deployment?.config?.hypervisorInterfaces || []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const { data } = await tenant.get(`/interface`);
      setData(
        data.filter((i) => Object.values(HYPERVISOR_TYPES).includes(i.type)),
      );
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          message="Die Hypervisor-Interfaces konnten nicht geladen werden"
        />,
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedHypervisorInterfaces(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleSave = () => {
    next({ hypervisorInterfaces: selectedHypervisorInterfaces });
  };

  const getHypervisorNameById = (id) => {
    const hypervisor = data.find((h) => h._id === id);
    return hypervisor ? hypervisor.description : id;
  };

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'visible',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <CardHeader
        avatar={<StorageIcon color="primary" />}
        title={<Typography variant="h6">Hypervisor Konfiguration</Typography>}
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />

      <CardContent sx={{ pt: 3, pb: 2, flex: 1 }}>
        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Es wurde noch kein Hypervisor konfiguriert
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                component={Link}
                href="/admin/interfaces"
                target="_blank"
                variant="outlined"
              >
                Hypervisor verwalten
              </Button>
              <Tooltip title="Hypervisor Liste aktualisieren">
                <IconButton size="small" onClick={fetchData} disabled={loading}>
                  <RefreshIcon
                    sx={{
                      animation: loading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}
            >
              <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
                Wählen Sie die Hypervisoren aus, die für die Host- und
                VM-Verwaltung verwendet werden sollen
              </Alert>
              <Button
                component={Link}
                href="/admin/interfaces"
                target="_blank"
                variant="outlined"
                size="small"
              >
                Hypervisor verwalten
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>Hypervisoren auswählen</InputLabel>
                <Select
                  multiple
                  value={selectedHypervisorInterfaces}
                  onChange={handleChange}
                  label="Hypervisoren auswählen"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={getHypervisorNameById(value)}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {data.map((hypervisor) => (
                    <MenuItem key={hypervisor._id} value={hypervisor._id}>
                      <Checkbox
                        checked={
                          selectedHypervisorInterfaces.indexOf(hypervisor._id) >
                          -1
                        }
                      />
                      <ListItemText primary={hypervisor.description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                sx={{ height: '56px', display: 'flex', alignItems: 'center' }}
              >
                <Tooltip title="Hypervisor Liste aktualisieren">
                  <IconButton
                    size="small"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <RefreshIcon
                      sx={{
                        animation: loading ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </>
        )}
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <FormButtonControls
          next={next && handleSave}
          back={back}
          cancel={handleCancel}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          disabled={selectedHypervisorInterfaces.length === 0}
        />
      </CardActions>
    </Card>
  );
};

export default HypervisorInterfacesConfig;
